import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { Feedback, Message, NotificationPrefs, UserProfile } from '../types'
import { generateUsername } from '../services/storage'

const defaultProfile = (): UserProfile => ({
  id: `demo-${Date.now().toString(36)}`,
  username: generateUsername(),
  email: '',
  phone: '',
  region: '中国大陆',
  signature: '铭记历史，珍爱和平。',
  avatar: '',
  realName: '',
  gender: '未设置',
  birthDate: '',
  city: '沈阳',
  occupation: '高校学生',
  bio: '',
})

const defaultPrefs: NotificationPrefs = {
  enabled: true, events: true, opening: true, news: true, education: true, artifacts: false,
}

interface MuseumState {
  isLoggedIn: boolean
  profile: UserProfile
  favorites: string[]
  history: string[]
  downloads: string[]
  registrations: string[]
  quizRecords: { id: string; score: number; date: string }[]
  notifications: NotificationPrefs
  feedbacks: Feedback[]
  messages: Message[]
  toast: string
  login: (email?: string) => void
  logout: () => void
  register: (email: string, username: string) => void
  updateProfile: (profile: Partial<UserProfile>) => void
  resetProfile: () => void
  toggleFavorite: (id: string) => void
  addHistory: (id: string) => void
  addDownload: (id: string) => void
  addRegistration: (id: string) => void
  saveQuiz: (score: number) => void
  updateNotifications: (prefs: Partial<NotificationPrefs>) => void
  addFeedback: (feedback: Omit<Feedback, 'id' | 'createdAt' | 'status'>) => void
  addMessage: (content: string) => void
  deleteAccount: () => void
  showToast: (message: string) => void
  clearToast: () => void
}

export const useMuseumStore = create<MuseumState>()(persist((set, get) => ({
  isLoggedIn: false,
  profile: defaultProfile(),
  favorites: [],
  history: [],
  downloads: [],
  registrations: [],
  quizRecords: [],
  notifications: defaultPrefs,
  feedbacks: [],
  messages: [],
  toast: '',
  login: (email) => set((state) => ({ isLoggedIn: true, profile: { ...state.profile, email: email || state.profile.email } })),
  logout: () => set({ isLoggedIn: false }),
  register: (email, username) => set((state) => ({ isLoggedIn: true, profile: { ...state.profile, email, username } })),
  updateProfile: (next) => set((state) => ({ profile: { ...state.profile, ...next } })),
  resetProfile: () => set({ profile: defaultProfile() }),
  toggleFavorite: (id) => set((state) => ({ favorites: state.favorites.includes(id) ? state.favorites.filter((item) => item !== id) : [...state.favorites, id] })),
  addHistory: (id) => set((state) => ({ history: [id, ...state.history.filter((item) => item !== id)].slice(0, 30) })),
  addDownload: (id) => set((state) => ({ downloads: [id, ...state.downloads.filter((item) => item !== id)] })),
  addRegistration: (id) => set((state) => ({ registrations: state.registrations.includes(id) ? state.registrations : [...state.registrations, id] })),
  saveQuiz: (score) => set((state) => ({ quizRecords: [{ id: crypto.randomUUID(), score, date: new Date().toISOString() }, ...state.quizRecords] })),
  updateNotifications: (prefs) => set((state) => ({ notifications: { ...state.notifications, ...prefs } })),
  addFeedback: (feedback) => set((state) => ({ feedbacks: [{ ...feedback, id: crypto.randomUUID(), createdAt: new Date().toISOString(), status: '待处理' }, ...state.feedbacks] })),
  addMessage: (content) => set((state) => ({ messages: [{ id: crypto.randomUUID(), username: state.profile.username, avatar: state.profile.avatar, content, createdAt: new Date().toISOString(), status: '审核中' }, ...state.messages] })),
  deleteAccount: () => set({ isLoggedIn: false, profile: defaultProfile(), favorites: [], history: [], downloads: [], registrations: [], quizRecords: [], notifications: defaultPrefs, feedbacks: [], messages: [] }),
  showToast: (message) => {
    set({ toast: message })
    window.setTimeout(() => get().clearToast(), 2600)
  },
  clearToast: () => set({ toast: '' }),
}), {
  name: 'museum918:state',
  storage: createJSONStorage(() => localStorage),
  partialize: (state) => ({ isLoggedIn: state.isLoggedIn, profile: state.profile, favorites: state.favorites, history: state.history, downloads: state.downloads, registrations: state.registrations, quizRecords: state.quizRecords, notifications: state.notifications, feedbacks: state.feedbacks, messages: state.messages }),
}))
